import { Injectable } from '@angular/core';
import { Store } from '../../../services/store';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { BomListProvider } from './bom-list.provider';
import { BomListItemDto } from './models/bom-list-item.dto';
import { BomListDto } from './models/bom-list.dto';
import { toVoid } from '../../../utils/void';
import { BomStructure } from '../../../models/bom-structure';

// Nothing changes it, so it is not state (0091)
export const PAGE_SIZE = 50;

export type BomListCriteria = Pick<BomListState, 'search' | 'typeFilter' | 'structureFilter' | 'page'>;

export interface BomListState {
  items: BomListItemDto[] | undefined;
  // the size of the whole narrowed result, not of the page
  totalCount: number;
  page: number;
  loading: boolean;
  search: string;
  typeFilter: BomListItemDto['type'] | undefined;
  structureFilter: BomStructure | undefined;
  // True when the tenant has no BOM items at all — as opposed to none
  // matching the current search/filter. Drives the "create your first
  // product" invitation, which must win over the no-results messages
  catalogEmpty: boolean;
}

// Products are the entry points of the catalog (materials are what you find
// inside them), so the list opens on Produkt; Wszystkie is one click away
const INITIAL_STATE: BomListState = {
  items: undefined,
  totalCount: 0,
  page: 1,
  loading: false,
  search: '',
  typeFilter: 'product',
  structureFilter: undefined,
  catalogEmpty: false,
};

@Injectable({ providedIn: 'root' })
export class BomListStore extends Store<BomListState> {
  constructor(private provider: BomListProvider) {
    super(INITIAL_STATE);
  }

  // The URL owns the criteria (0092): the page hands over all four at once
  // and the store mirrors them instead of accumulating gestures. Only the
  // first load shows Loading…, later ones keep the rows until the answer
  load(criteria: BomListCriteria): Observable<void> {
    this.update(state => ({ ...state, ...criteria, loading: state.items === undefined }));

    return this.fetch();
  }

  // Search, filter and page always travel together, so every load (including
  // the initial one) re-queries with all of them — state and request cannot
  // drift. The server orders by name; its order is the order
  private fetch(): Observable<void> {
    const { search, typeFilter, structureFilter, page } = this.state;

    return this.provider.getBomList(search || undefined, typeFilter, structureFilter, page, PAGE_SIZE)
      .pipe(
        switchMap(list => this.withCatalogEmpty(list, !!search || !!typeFilter || !!structureFilter)),
        tap(({ list, catalogEmpty }) => this.update(state => ({
          ...state,
          items: list.items,
          totalCount: list.totalCount,
          catalogEmpty,
          loading: false
        }))),
        toVoid()
      );
  }

  // A narrowed query that comes back empty cannot tell "no products" from
  // "no items at all", so probe once without criteria — only in that case,
  // an unnarrowed result already answers the question with its count
  private withCatalogEmpty(list: BomListDto, narrowed: boolean): Observable<{ list: BomListDto, catalogEmpty: boolean }> {
    if (list.totalCount > 0 || !narrowed) {
      return of({ list, catalogEmpty: list.totalCount === 0 });
    }

    return this.provider.getBomList(undefined, undefined, undefined, undefined, 1)
      .pipe(map(all => ({ list, catalogEmpty: all.totalCount === 0 })));
  }
}
